import { useAuthStore } from '@/stores/auth'
import { useCustomerStore } from '@/stores/customer'
import { useSignupStore } from '@/stores/signup'
import { usePaymentMethodStore } from '@/stores/paymentMethod'
import type { NavigationGuardNext, RouteLocationNormalizedGeneric } from 'vue-router'
import type { RouteMetaCustom } from '../types'
import createLogger from '@/utils/logger'
import { AuthPages } from '../authRoutes'

import { AdminPages } from '../adminRoutes'
import { CustomerPages } from '../customerRoutes'
import { SignupPages } from '../signupRoutes'
import { hardReloadOnStoreError } from '@/utils/storeUtils'
import { isConsultationRoute } from '../routeMeta'
import { loginPathWithRedirect, parsePostLoginRedirectPath } from '@/utils/postLoginRedirect'

const logger = createLogger('router/guards/redirects.ts')

export async function redirects(
  to: RouteLocationNormalizedGeneric,
  _from: RouteLocationNormalizedGeneric,
  next: NavigationGuardNext,
) {
  const authStore = useAuthStore()
  const customerStore = useCustomerStore()
  const signupStore = useSignupStore()
  const hasMatchedRoute = to.matched.length > 0
  const requiresAuth = to.matched.some((record) => (record.meta as RouteMetaCustom).requiresAuth)
  const isSystemUserRoute = to.matched.some((record) => record.meta.isSystemUser)
  const isCustomerRoute = to.matched.some((record) => (record.meta as RouteMetaCustom).isCustomer)
  const isSignupRoute = to.matched.some((record) => (record.meta as RouteMetaCustom).isSignup)

  if (!hasMatchedRoute) {
    logger.log('Unknown route, redirecting to root')
    next('/')
    return
  }

  if (authStore.isLoading) {
    logger.log('authStore loading, should not happen')
    try {
      await hardReloadOnStoreError()
    } catch (error) {
      logger.error('Error hard reloading on store error', error)
      await authStore.signOut({
        redirectToLogin: true,
        errorMessage: 'An error occurred while loading the app. Please try to login again.',
        hardReloadToLogin: true,
      })
      return
    }
    return
  }

  // Handle root path redirect for unauthenticated users
  if (to.path === '/' && !authStore.isAuthenticated) {
    logger.log('Unauthenticated user on root path, redirecting to login')
    next(AuthPages.Login.path)
    return
  }

  // Handle root path redirect with proper role checking for authenticated users
  if (to.path === '/' && authStore.isAuthenticated) {
    logger.log('Authenticated user on root path, waiting for role claims...')
    next(authStore.isSystemUser ? AdminPages.Dashboard.path : CustomerPages.Home.path)
    return
  }

  if (requiresAuth && !authStore.isAuthenticated) {
    const redirectPath = parsePostLoginRedirectPath(to.fullPath)
    if (redirectPath) {
      logger.log('Unauthenticated protected route, redirecting to login with return path')
      next(loginPathWithRedirect(redirectPath))
      return
    }
    logger.log('requiresAuth && !authStore.isAuthenticated, redirecting to login')
    next(AuthPages.Login.path)
    return
  }
  if (to.path === '/login' && authStore.isAuthenticated) {
    const redirectPath = parsePostLoginRedirectPath(to.query?.redirect)
    if (redirectPath) {
      logger.log('Authenticated user on login with post-login redirect')
      next(redirectPath)
      return
    }
    logger.log('to.path === /login && authStore.isAuthenticated, redirecting to /')
    next('/')
    return
  }

  // System user routes are only accessible to system users
  if (authStore.isSystemUser && isCustomerRoute) {
    logger.log('isCustomerRoute && !authStore.isCustomer, redirecting to admin')
    // Redirect admin users away from customer-only routes
    next(AdminPages.Dashboard.path)
    return
  }

  // Customer routes are only accessible to customers
  if (authStore.isCustomer) {
    if (customerStore.isLoading) {
      logger.log('customerStore loading timout, doing hard reload')
      try {
        await hardReloadOnStoreError()
      } catch (error) {
        logger.error('Error hard reloading on store error', error)
        await authStore.signOut({
          redirectToLogin: true,
          errorMessage: 'An error occurred while loading the app. Please try to login again.',
          hardReloadToLogin: true,
        })
        return
      }
      return
    }

    if (isSystemUserRoute) {
      logger.log('isAdminRoute && authStore.isCustomer, redirecting to profile')
      // Redirect non-admin users to profile page
      next(CustomerPages.Home.path)
      return
    }

    if (customerStore.signupCompleted && isSignupRoute) {
      if (to.name === SignupPages.SignupCompletedMessage.name) {
        logger.log('Customer completed signup on final signup page, allowing route')
        next()
        return
      }
      if (
        signupStore.googleSignupDuplicateCheckInFlight ||
        to.name === SignupPages.EmailAlreadyExists.name
      ) {
        next()
        return
      }
      logger.log('Customer is signup completed, redirecting to home')
      next(CustomerPages.Home.path)
      return
    }
    if (!customerStore.signupCompleted) {
      await signupStore.untilLoadingIsDone()

      const redirectPath = signupStore.getSignupEntryRedirectPath(to.path, { isSignupRoute })
      if (redirectPath) {
        logger.log('Signup entry redirect to', redirectPath)
        next(redirectPath)
        return
      }
    }

    if (customerStore.signupCompleted && isConsultationRoute(to)) {
      const paymentMethodStore = usePaymentMethodStore()
      if (paymentMethodStore.subscriptionFrom === 'sponsorInfo') {
        logger.log('Sponsored customer on consultation route, redirecting to root')
        next('/')
        return
      }
    }
  }

  logger.log('no redirect', to.path)
  next()
}
